import {
  Box,
  Checkbox,
  Link,
  TableCell,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import moment from 'moment';
import { setNotification } from '@store/features/notification';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
  downloadDocument,
  updateDocumentStatus,
} from '@features/workspace/store/actions';
import { StatusInfo } from '@features/workspace/components/shared/status-info';
import { makeStyles } from 'tss-react/mui';
import { setIsLoading } from '@store/features/loading';
import { useCallback, useMemo } from 'react';
import history from '@app/services/history';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  UserRoleEnum,
  DocumentStatusEnum,
  DocumentTypeLabelEnum,
  EmployeePositionEnum,
  UNKNOWN,
  GENERAL_ERROR_MESSAGE,
  DocumentTypeEnum,
} from '@utils/constants';
import { selectUsersData } from '@features/workspace/store/selectors';
import config from '@/app/configs/config';

const useStyles = makeStyles<any>()({
  link: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '12px',
  },
  lastRow: {
    td: {
      borderBottom: '0px !important',
    },
  },
});

export function DocumentTableRow({
  document,
  isLastRow,
  rejectedMode = false,
}: any) {
  const usersData = useAppSelector(selectUsersData);
  const userData = usersData.find((user: any) => user.id === document.userId);

  const isDocumentApproved =
    document.zendeskStatus === DocumentStatusEnum.Approved;
  const IsDocumentRejected =
    document.zendeskStatus === DocumentStatusEnum.Rejected;
  const isDocumentExpired =
    document.documentType === DocumentTypeEnum.TemporaryID &&
    document.expiryDate &&
    new Date(document.expiryDate) >= new Date();

  const role = useMemo(() => {
    if (
      userData &&
      userData.assignment?.position !== null &&
      userData.assignment?.position !== undefined
    ) {
      return userData.assignment?.position === EmployeePositionEnum.AuthPerson
        ? UserRoleEnum.AuthPerson
        : UserRoleEnum.Director;
    }
    if (document.userId) {
      return UserRoleEnum.Agent;
    }

    return null;
  }, [userData, document]);

  const { classes } = useStyles(null);

  const dispatch = useAppDispatch();

  const setDocumentStatus = async (
    currentDocument: any,
    status: any,
    notificationActionText: any
  ) => {
    try {
      await dispatch(
        updateDocumentStatus({
          documentId: currentDocument?.id,
          status,
        })
      ).unwrap();
      dispatch(
        setNotification({
          message: `Document ${
            DocumentTypeLabelEnum[currentDocument.documentType]
          } has been ${notificationActionText}`,
          variant: 'info',
        })
      );
    } catch (err) {
      dispatch(
        setNotification({ message: GENERAL_ERROR_MESSAGE, variant: 'error' })
      );
    }
  };

  const onApprove = () => {
    setDocumentStatus(
      document,
      DocumentStatusEnum.Approved,
      'marked as approved'
    );
  };

  const onReject = () => {
    setDocumentStatus(
      document,
      DocumentStatusEnum.Rejected,
      'marked as rejected'
    );
  };

  const onDownload = async () => {
    dispatch(setIsLoading(true));
    dispatch(
      setNotification({
        message: 'Your document will be downloaded in a moment...',
        variant: 'info',
      })
    );
    try {
      await dispatch(downloadDocument(document)).unwrap();
    } catch (err) {
      dispatch(
        setNotification({
          message: 'Download failed.',
          variant: 'error',
        })
      );
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const onOpenDocumentInJumio = () => {
    if (document.jumioLink) {
      dispatch(
        setNotification({ message: 'Forwarding to Jumio...', variant: 'info' })
      );
      window.open(new URL(document.jumioLink).href, 'JumioExternal');
    }
  };

  const onResetDocument = async () => {
    setDocumentStatus(document, DocumentStatusEnum.Submitted, 'unarchived');
  };

  const onInternalClick = useCallback(() => {
    const uri =
      role === UserRoleEnum.Director
        ? `/customer/company/directors/${userData?.id}/edit`
        : `/customer/company/auth-persons/${userData?.id}/edit`;

    history.push(uri);
  }, [userData, role]);

  // Document owner full name
  const contactFullName = useMemo(
    () =>
      userData
        ? [userData?.person?.firstName, userData?.person?.lastName].join(' ')
        : null,
    [userData]
  );

  return (
    <>
      <TableRow className={isLastRow ? classes.lastRow : ''}>
        {/* Date */}
        <TableCell>
          {moment(document.modifiedAt).format(config.dateFormat)}
        </TableCell>
        {/* Submitted By */}
        <TableCell>
          {role &&
          [UserRoleEnum.Director, UserRoleEnum.AuthPerson].includes(role) ? (
            <Link
              sx={{ cursor: 'pointer', fontSize: '12px' }}
              title={`Edit ${
                role === UserRoleEnum.Director
                  ? 'director'
                  : 'authorised person'
              }`}
              onClick={onInternalClick}
            >
              {contactFullName || document.modifiedByName}
            </Link>
          ) : (
            <Box display="flex" flexDirection="row" ml={0}>
              <Box>{document.modifiedByName || contactFullName}</Box>
              {role === UserRoleEnum.Agent && (
                <Box sx={{ paddingLeft: '5px', wordBreak: 'keep-all' }}>
                  <Typography
                    className="labelHeader"
                    sx={{ color: '#838383' }}
                    variant="subtitle2"
                  >
                    (Agent)
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </TableCell>
        {/* Document Type */}
        <TableCell>
          <Box alignItems="center" display="flex" flexDirection="row">
            <Box
              alignItems="center"
              display="flex"
              style={{ cursor: 'pointer' }}
              onClick={onOpenDocumentInJumio}
            >
              <Tooltip title="Open document in Jumio">
                <Link className={classes.link}>
                  {DocumentTypeLabelEnum[document.documentType] || UNKNOWN}
                  {document.documentType === DocumentTypeEnum.TemporaryID && (
                    <>
                      &nbsp;(Expires:{' '}
                      {document.expiryDate &&
                        new Date(document.expiryDate).toLocaleDateString(
                          'en-CA',
                          { year: 'numeric', month: '2-digit', day: '2-digit' }
                        )}
                      )
                    </>
                  )}
                </Link>
              </Tooltip>
              &nbsp;
              {document.jumioLink && <OpenInNewIcon fontSize="small" />}
            </Box>
            <Box ml={1}>
              <Tooltip title="Download document">
                <IconButton size="small" onClick={onDownload}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box ml={2}>
              {!!document?.isVerified && (
                <StatusInfo
                  isCompleted={document?.isVerified ?? false}
                  showIcon={false}
                  text="PASS"
                />
              )}
            </Box>
          </Box>
        </TableCell>
        {rejectedMode && (
          <>
            <TableCell>
              {moment(document.date).format(config.dateFormat)}
            </TableCell>
            <TableCell>
              <Link className={classes.link} onClick={onResetDocument}>
                Undo archive
              </Link>
            </TableCell>
          </>
        )}
        {!rejectedMode && (
          <>
            {/* Approve */}
            <TableCell align="center">
              {isDocumentApproved && (
                <Box display="flex" flexDirection="column">
                  <Box>
                    <StatusInfo
                      showIcon
                      isCompleted={!isDocumentExpired}
                      text={isDocumentExpired ? 'Expired' : 'Accepted'}
                    />
                  </Box>
                  <Box>
                    {/* TODO: use modifiedOn date once split has been made between createdOn and modifiedOn  */}
                    {isDocumentExpired
                      ? moment(document.expiryDate).format(config.dateFormat)
                      : moment(document.date).format(config.dateFormat)}
                  </Box>
                </Box>
              )}
              {!isDocumentApproved && (
                <Checkbox checked={isDocumentApproved} onClick={onApprove} />
              )}
            </TableCell>
            {/* Decline */}
            <TableCell align="center">
              <Checkbox checked={IsDocumentRejected} onClick={onReject} />
            </TableCell>
          </>
        )}
      </TableRow>
    </>
  );
}
